/* eslint-disable jsx-a11y/prefer-tag-over-role */
import { useSpiritoryModal } from '@/context/SpiritoryModalProvider';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { FaGift } from 'react-icons/fa';
import DefaultButton from '../common/default/DefaultButton';
import LanguageSwitcher from '../languageSwitcher';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import parseWithType from '@/utilites/parse-with-type';
import cookiesConstants from '../../constants/cookies.constants';
import useBackendUser from '@/hooks/useBackendUser';

export default function ReferralsFloatingIcon() {
  const { t: tCommon } = useTranslation('common');
  const modal = useSpiritoryModal();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralCodeValid, setReferralCodeValid] = useState<boolean>(false);
  const [referralType, setReferralType] = useState<'INFLUENCER' | 'FRIENDS' | null>(null);
  const { data: user = null, isLoading: isLoadingUser } = useBackendUser();

  const openReferralSuccessModalInfluencer = (referralCodeParam: string) => {
    modal.setModalType('info');
    modal.setTitle(tCommon('referrals.success.referrals_influencer'));
    modal.setSubmitText(tCommon('referrals.sign_up'));
    modal.setSubmitAction(
      () => () => (window.location.href = `/api/referrals/${referralCodeParam}/pre-redeem`)
    );
    modal.setBody(
      <>
        <div className="tw-px-6 tw-py-4 tw-text-center">
          <h2 className="tw-mb-4 tw-text-2xl tw-font-bold">
            <span role="img" aria-label="sparkles">
              🌟
            </span>{' '}
            <span className="tw-text-blue-600">{tCommon('referrals.success.welcome')}!</span>{' '}
            <span role="img" aria-label="sparkles">
              🌟
            </span>
          </h2>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            <span role="img" aria-label="video camera">
              🎥
            </span>{' '}
            {tCommon('referrals.success.you_have_been_referred_influencer')}{' '}
            <span role="img" aria-label="speech bubble">
              💬
            </span>{' '}
            {tCommon('referrals.success.they_re_inviting_you_to_join_influencer')}{' '}
            <span role="img" aria-label="gift">
              🎁
            </span>
          </p>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            <span role="img" aria-label="memo">
              📝
            </span>{' '}
            <span className="tw-font-semibold">
              {tCommon('referrals.success.sign_up_now_influencer')}
            </span>{' '}
            {tCommon('referrals.success.to_unlock_your_special_gift_influencer')}{' '}
            <span role="img" aria-label="rocket">
              🚀
            </span>
          </p>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            <span role="img" aria-label="pointing right">
              👉
            </span>{' '}
            <span className="tw-font-semibold">
              {tCommon('referrals.success.click_sign_up_below_influencer')}
            </span>{' '}
            {tCommon('referrals.success.to_get_started_influencer')}
          </p>
          <p className="tw-text-base tw-italic tw-text-gray-600">
            <span role="img" aria-label="light bulb">
              💡
            </span>{' '}
            {tCommon('referrals.success.dont_wait_too_long_influencer')}
          </p>
          <div className="tw-flex tw-justify-center">
            <LanguageSwitcher />
          </div>
        </div>
      </>
    );

    modal.openModal();
  };

  const openReferralInvalidModalInfluencer = () => {
    modal.setModalType('info');
    modal.setSubmitText(tCommon('referrals.ok'));
    modal.setTitle(tCommon('referrals.error.invalid_referral_influencer'));
    modal.setBody(
      <>
        <div className="tw-px-6 tw-py-4 tw-text-center">
          <h2 className="tw-mb-4 tw-text-2xl tw-font-bold">
            <span role="img" aria-label="warning">
              ⚠️
            </span>{' '}
            <span className="tw-text-red-600">{tCommon('referrals.error.oops_influencer')}</span>{' '}
            <span role="img" aria-label="warning">
              ⚠️
            </span>
          </h2>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            {tCommon('referrals.error.link_invalid_influencer')}{' '}
            <span role="img" aria-label="confused face">
              😕
            </span>
          </p>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            {tCommon('referrals.error.please_double_check_influencer')}{' '}
            <span role="img" aria-label="lifebuoy">
              🛟
            </span>
          </p>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            <span role="img" aria-label="retry">
              🔄
            </span>{' '}
            {tCommon('referrals.error.try_again_influencer')}{' '}
            <span className="tw-font-semibold">
              {tCommon('referrals.error.get_fresh_link_influencer')}
            </span>
            .
          </p>
          <p className="tw-text-base tw-italic tw-text-gray-600">
            <span role="img" aria-label="light bulb">
              💡
            </span>{' '}
            {tCommon('referrals.error.need_help_influencer')}
          </p>
        </div>
      </>
    );
    deleteCookie(cookiesConstants.COOKIES.REFERRAL_CODE.name);
    modal.openModal();
  };

  const openReferralSuccessModalFriends = (referralCodeParam: string) => {
    modal.setModalType('info');
    modal.setTitle(tCommon('referrals.friends.success.referrals_friend'));
    modal.setSubmitText(tCommon('referrals.sign_up'));
    modal.setSubmitAction(
      () => () => (window.location.href = `/api/referrals/${referralCodeParam}/pre-redeem`)
    );
    modal.setBody(
      <>
        <div className="tw-px-6 tw-py-4 tw-text-center">
          <h2 className="tw-mb-4 tw-text-2xl tw-font-bold">
            <span role="img" aria-label="sparkles">
              🌟
            </span>{' '}
            <span className="tw-text-blue-600">
              {tCommon('referrals.friends.success.welcome')}!
            </span>{' '}
            <span role="img" aria-label="sparkles">
              🌟
            </span>
          </h2>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            <span role="img" aria-label="video camera">
              🎥
            </span>{' '}
            {tCommon('referrals.friends.success.you_have_been_referred_friend')}{' '}
            <span role="img" aria-label="speech bubble">
              💬
            </span>{' '}
            {tCommon('referrals.friends.success.they_re_inviting_you_to_join_friend')}{' '}
            <span role="img" aria-label="gift">
              🎁
            </span>
          </p>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            <span role="img" aria-label="memo">
              📝
            </span>{' '}
            <span className="tw-font-semibold">
              {tCommon('referrals.friends.success.sign_up_now_friend')}
            </span>{' '}
            {tCommon('referrals.friends.success.to_unlock_your_special_gift_friend')}{' '}
            <span role="img" aria-label="rocket">
              🚀
            </span>
          </p>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            <span role="img" aria-label="pointing right">
              👉
            </span>{' '}
            <span className="tw-font-semibold">
              {tCommon('referrals.friends.success.click_sign_up_below_friend')}
            </span>{' '}
            {tCommon('referrals.friends.success.to_get_started_friend')}
          </p>
          <p className="tw-text-base tw-italic tw-text-gray-600">
            <span role="img" aria-label="light bulb">
              💡
            </span>{' '}
            {tCommon('referrals.friends.success.dont_wait_too_long_friend')}
          </p>
          <div className="tw-flex tw-justify-center">
            <LanguageSwitcher />
          </div>
        </div>
      </>
    );

    modal.openModal();
  };

  const openReferralInvalidModalFriends = () => {
    modal.setModalType('info');
    modal.setSubmitText(tCommon('referrals.ok'));
    modal.setTitle(tCommon('referrals.friends.error.invalid_referral_friend'));
    modal.setBody(
      <>
        <div className="tw-px-6 tw-py-4 tw-text-center">
          <h2 className="tw-mb-4 tw-text-2xl tw-font-bold">
            <span role="img" aria-label="warning">
              ⚠️
            </span>{' '}
            <span className="tw-text-red-600">
              {tCommon('referrals.friends.error.oops_friend')}
            </span>{' '}
            <span role="img" aria-label="warning">
              ⚠️
            </span>
          </h2>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            {tCommon('referrals.friends.error.link_invalid_friend')}{' '}
            <span role="img" aria-label="confused face">
              😕
            </span>
          </p>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            {tCommon('referrals.friends.error.please_double_check_friend')}{' '}
            <span role="img" aria-label="lifebuoy">
              🛟
            </span>
          </p>
          <p className="tw-mb-4 tw-text-lg tw-text-gray-700">
            <span role="img" aria-label="retry">
              🔄
            </span>{' '}
            {tCommon('referrals.friends.error.try_again_friend')}{' '}
            <span className="tw-font-semibold">
              {tCommon('referrals.friends.error.get_fresh_link_friend')}
            </span>
            .
          </p>
          <p className="tw-text-base tw-italic tw-text-gray-600">
            <span role="img" aria-label="light bulb">
              💡
            </span>{' '}
            {tCommon('referrals.friends.error.need_help_friend')}
          </p>
        </div>
      </>
    );

    deleteCookie(cookiesConstants.COOKIES.REFERRAL_CODE.name);

    modal.openModal();
  };

  useEffect(() => {
    if (isLoadingUser === false) {
      if (user === null) {
        const referralCookie = getCookie(cookiesConstants.COOKIES.REFERRAL_CODE.name);

        const parsedReferralCookie = parseWithType<{
          code: string;
          valid: boolean;
          type: 'FRIENDS' | 'INFLUENCER' | null;
          showModal: boolean;
        } | null>(referralCookie || 'null');

        if (parsedReferralCookie) {
          if (parsedReferralCookie.code !== 'Whiskyfestival10') {
            setReferralCode(parsedReferralCookie.code);
            setReferralCodeValid(parsedReferralCookie.valid);
            setReferralType(parsedReferralCookie.type);

            setCookie(
              cookiesConstants.COOKIES.REFERRAL_CODE.name,
              JSON.stringify({
                code: parsedReferralCookie.code,
                valid: parsedReferralCookie.valid,
                type: parsedReferralCookie.type,
                showModal: false,
              }),
              {
                maxAge: 60 * 60 * 24 * 7,
              }
            );

            if (parsedReferralCookie.type === 'INFLUENCER') {
              if (parsedReferralCookie.valid) {
                openReferralSuccessModalInfluencer(parsedReferralCookie.code);
              } else {
                openReferralInvalidModalInfluencer();
              }
            } else {
              if (parsedReferralCookie.valid) {
                openReferralSuccessModalFriends(parsedReferralCookie.code);
              } else {
                openReferralInvalidModalFriends();
              }
            }
          }
        }
      } else {
        setReferralCode(null);
        setReferralCodeValid(false);
        setReferralType(null);
      }
    }
  }, [user, isLoadingUser]);

  if (referralCodeValid === false || !referralCode) {
    return null;
  }

  return (
    <div className="tw-fixed tw-bottom-4 tw-right-4 tw-z-50">
      <DefaultButton
        className="tw-flex tw-transform tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-500 tw-p-4 tw-shadow-lg tw-transition hover:tw-scale-110 focus:tw-outline-none"
        onClick={() => {
          if (referralType === 'INFLUENCER') {
            openReferralSuccessModalInfluencer(referralCode);
          } else {
            openReferralSuccessModalFriends(referralCode);
          }
        }}
      >
        <FaGift className="tw-text-xl tw-text-white" />
      </DefaultButton>
    </div>
  );
}
